import _ from '@/common/helpers';
import {DateTimeFormats} from '@/common/datetime-formats';
import EntityModel from '@/common/models/entity-model';
import TimelineMessageDataModel from './timeline-message-data-model';

export default class TimelineMessageModel extends EntityModel {
    hydrate({
        id = null,
        message = null,
        triggeredBy = null,
        extraData = null,
        occurredTime = null,
        type = null,
        attachments = [],
    } = {}) {
        this.id = id;
        this.message = message;
        this.triggeredBy = triggeredBy;
        this.extraData = new TimelineMessageDataModel(extraData);
        this.occurredTime = occurredTime;
        this.type = type;
        this.attachments = attachments;
    }

    get occurredTimeMoment() {
        return TimelineMessageModel.datetimeToMoment(this.occurredTime);
    }

    get time() {
        return this.occurredTimeMoment.format(DateTimeFormats.timelineTime);
    }

    get dateTime() {
        return this.occurredTimeMoment.format(DateTimeFormats.timelineDateTime);
    }

    get hasActions() {
        return this.extraData.hasActions;
    }

    get hasTables() {
        return this.extraData.hasTables;
    }

    get hasDetails() {
        return this.hasActions || this.hasTables;
    }

    get hasAttachments() {
        return !!this.attachments.length;
    }

    get author() {
        return this.extraData.author;
    }

    get mentions() {
        return this.extraData.mentions;
    }

    get anchorId() {
        return `timeline-message-${this.id}`;
    }

    getUserFriendlyPayload() {
        if (this.type === TimelineMessageModel.Types.gatewayResponseReceived) {
            const transforms = [
                [
                    value => _.isJSONString(value),
                    value => JSON.stringify(JSON.parse(value), null, 2),
                ],
                [
                    value => _.isXMLString(value),
                    value => new XMLSerializer().serializeToString(_.stringToXML(value)),
                ],
            ];
            const transform = (val) => {
                try {
                    const [, data] = transforms.find(([condition]) => condition(val));
                    return data(val);
                } catch (err) {
                    return String(val);
                }
            };

            const payload = this.extraData.tables.reduce((memo, table) => ({
                ...memo,
                ...table.data.reduce((m, item) => ({
                    ...m,
                    [item.attribute]: item.attribute === 'Duration' ? `${item.value} ms` : item.value,
                }), {}),
            }), {});

            const response = Object
                .keys(payload)
                .reduce(
                    (memo, key) => {
                        memo += `${key}: ${transform(payload[key])}\r\n`;
                        return memo;
                    },
                    '',
                );

            const header = [
                `Occurred Time: ${this.occurredTime}`,
                `Transaction Id: ${this.$specialProps.entityId}`,
            ].join('\r\n');

            return `${header}\r\n\r\n${response}`;
        }
        throw new Error(`Please implement functionality`);
    }

    static Types = {
        gatewayAccountCreated: 'gateway-account-created',
        gatewayAccountChanged: 'gateway-account-changed',
        gatewayAccountEnabled: 'gateway-account-enabled',
        gatewayAccountDisabled: 'gateway-account-disabled',
        gatewayAccountDown: 'gateway-account-is-down',
        gatewayAccountUp: 'gateway-account-is-up',
        gatewayAccountCommentCreated: 'gateway-account-comment-created',
        gatewayResponseReceived: 'gateway-response-received',
        customerCommentCreated: 'customer-comment-created',
        timelineCommentCreated: 'timeline-comment-created',
        emailMessageSent: 'email-message-sent',
        kycDocumentCreated: 'kyc-document-created',
        kycDocumentManuallyAccepted: 'kyc-document-manually-accepted',
    };

    static TriggerSources = {
        rebilly: 'rebilly',
        application: 'app',
        API: 'direct-api',
    };
}
